// components/industry-templates/AgricultureEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sprout, Tractor, Users, Award } from 'lucide-react';

export const AgricultureEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Agri-Tech Showcase */}
      {industryData?.agritech_showcase && (
        <Card className="border-0 bg-gradient-to-br from-lime-500/20 to-green-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Tractor className="w-8 h-8" style={{ color: theme.primary }} />
              Agricultural Technology Showcase
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.agritech_showcase.map((tech: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border border-white/20">
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">🚜</div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">{tech.name}</h3>
                      <p className="text-slate-300 text-sm mb-3">{tech.description}</p>
                      <div className="flex gap-2">
                        <Badge style={{ backgroundColor: theme.primary }}>{tech.category}</Badge>
                        {tech.live_demo && (
                          <Badge className="bg-yellow-500 text-black">Live Demo</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Farming Techniques */}
      {industryData?.farming_techniques && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Sprout className="w-6 h-6" style={{ color: theme.primary }} />
              Modern Farming Techniques
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.farming_techniques.map((technique: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-2">{technique.name}</h4>
                  <p className="text-sm text-slate-400 mb-2">{technique.description}</p>
                  {technique.workshop_available && (
                    <Badge style={{ backgroundColor: theme.primary }}>
                      Workshop Available
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Crop Varieties */}
      {industryData?.crop_varieties && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white">Seed & Crop Varieties</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {industryData.crop_varieties.map((crop: string, idx: number) => (
                <div key={idx} className="p-3 bg-white/5 rounded-lg text-center border border-white/10">
                  <div className="text-2xl mb-2">🌾</div>
                  <p className="text-white font-medium text-sm">{crop}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expert Sessions */}
      {industryData?.expert_sessions && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(101, 163, 13, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-6 h-6" style={{ color: theme.primary }} />
              Expert Sessions & Workshops
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.expert_sessions.map((session: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-bold text-white">{session.topic}</h4>
                      <p className="text-sm text-slate-400">{session.expert}</p>
                    </div>
                    <Badge style={{ backgroundColor: theme.primary }}>
                      {session.time}
                    </Badge>
                  </div>
                  {session.certification && (
                    <Badge className="mt-2 bg-yellow-500 text-black">
                      Certificate Provided
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subsidies & Schemes */}
      {industryData?.government_schemes && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: theme.primary }} />
              Government Schemes & Subsidies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-5">
              <p className="text-white mb-3">Information desk available for:</p>
              <div className="flex flex-wrap gap-2">
                {industryData.government_schemes.map((scheme: string, idx: number) => (
                  <Badge key={idx} variant="outline" className="border-white/20 text-white">
                    {scheme}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
